// ---------------------------------------------------------------------------
// Drill-Down Configuration
// ---------------------------------------------------------------------------
// Defines relationships between data sources for drill-down navigation.
// Each relation specifies which column triggers drill-down and how to filter.
// ---------------------------------------------------------------------------

import type { DataSourceId } from './types';
import type { DimensionConfig } from '@/components/v11/funnel/constants';
import { AUDIENCE_DIMENSIONS, CONTENT_DIMENSIONS } from '@/components/v11/funnel/constants';

const ALL_DIMENSIONS: readonly DimensionConfig[] = [...AUDIENCE_DIMENSIONS, ...CONTENT_DIMENSIONS];

/**
 * Configuration for a drill-down relationship between data sources
 */
export interface DrillDownRelation {
  /** Source data source (table where user clicks) */
  readonly source: DataSourceId;
  /** Target data source (table to navigate to) */
  readonly target: DataSourceId;
  /** Column key that triggers drill-down when clicked */
  readonly sourceColumn: string;
  /** API parameter name for filtering target data source (legacy, used when no filterDimensionKey) */
  readonly apiParam: string;
  /** Label prefix for the drill-down badge (e.g., 'Profile') */
  readonly labelPrefix: string;
  /** Key in row.original to get the filter value (usually an ID) */
  readonly valueKey: string;
  /** Key in row.original to get the display label (e.g., username) */
  readonly displayKey: string;
  /** Filter dimension key to use with the regular filter system (e.g., 'post', 'brand', 'team').
   *  When present, drill-down adds a regular filter chip instead of a DrillDownBadge.
   *  When absent, falls back to legacy DrillDownBadge behavior. */
  readonly filterDimensionKey?: string;
}

/**
 * All supported drill-down relations
 * Add new relations here to enable drill-down between data sources
 */
export const DRILL_DOWN_RELATIONS: ReadonlyArray<DrillDownRelation> = [
  // Profile -> Content (click on Posts column)
  {
    source: 'profiles',
    target: 'content',
    sourceColumn: 'posts',
    apiParam: 'author_id',
    labelPrefix: 'Profile',
    valueKey: 'id',
    displayKey: 'username',
    filterDimensionKey: 'author',
  },
  // Profile -> Comments (click on Comments column)
  {
    source: 'profiles',
    target: 'comments',
    sourceColumn: 'comments',
    apiParam: 'author_id',
    labelPrefix: 'Profile',
    valueKey: 'id',
    displayKey: 'username',
    filterDimensionKey: 'author',
  },
  // Brand -> Profiles (click on Profiles column)
  {
    source: 'brands',
    target: 'profiles',
    sourceColumn: 'profiles',
    apiParam: 'brand_id',
    labelPrefix: 'Brand',
    valueKey: 'id',
    displayKey: 'name',
    filterDimensionKey: 'brand',
  },
  // Team -> Profiles (click on Profiles column)
  {
    source: 'teams',
    target: 'profiles',
    sourceColumn: 'profiles',
    apiParam: 'team_id',
    labelPrefix: 'Team',
    valueKey: 'id',
    displayKey: 'name',
    filterDimensionKey: 'team',
  },
  // Content -> Comments (click on Comments column)
  {
    source: 'content',
    target: 'comments',
    sourceColumn: 'comments',
    apiParam: 'post_id',
    labelPrefix: 'Post',
    valueKey: 'id',
    displayKey: 'caption',
    filterDimensionKey: 'post',
  },
];

/**
 * Find a drill-down relation for a given source and column
 * @param source - Current data source
 * @param column - Column key that was clicked
 * @returns DrillDownRelation if found, undefined otherwise
 */
export function getDrillDownRelation(
  source: DataSourceId,
  column: string
): DrillDownRelation | undefined {
  return DRILL_DOWN_RELATIONS.find(r => r.source === source && r.sourceColumn === column);
}

/**
 * Check if a column supports drill-down for a given data source
 * @param source - Current data source
 * @param column - Column key to check
 * @returns true if drill-down is available
 */
export function hasDrillDown(source: DataSourceId, column: string): boolean {
  return DRILL_DOWN_RELATIONS.some(r => r.source === source && r.sourceColumn === column);
}

/**
 * Get the filter DimensionConfig for a drill-down relation.
 * Returns the DimensionConfig when the relation has a filterDimensionKey,
 * allowing drill-down to add a regular filter instead of a DrillDownBadge.
 *
 * @param source - Current data source
 * @param column - Column key that was clicked
 * @returns DimensionConfig if relation has filterDimensionKey, undefined otherwise
 */
export function getFilterDimensionForDrillDown(
  source: DataSourceId,
  column: string
): DimensionConfig | undefined {
  const relation = getDrillDownRelation(source, column);
  if (!relation?.filterDimensionKey) return undefined;
  return ALL_DIMENSIONS.find(d => d.key === relation.filterDimensionKey);
}
